import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { PageLoader, SETVIImage } from '@setvi/shared/components';
import { CatalogConfig, ProductColumnsIds } from '@setvi/shared/interfaces';
import { getCustomerByUniqueValue } from '@setvi/shared/services';
import { ROUTES } from 'enumsV2';
import { Views } from 'Styles';
import { Box } from '@material-ui/core';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import Products from '..';
import { useCatalogConfig } from '../hooks/useCatalogConfig';
import { InvalidConfiguration } from '../components';

const FALLBACK_LOGO =
  'https://setvi.azureedge.net/public/Setvi/Logos/PNG/SETVI-Horizontal-Logo-RGB-FullColor_Transparent1x.png';

export interface CustomerViewSearchConfig {
  customerAccountNumber: string;
  filters: {
    customer: { show: false };
  };
  product: {
    columns: string[];
  };
  productDetails: {
    pricing: string[];
    tabs: string[];
  };
}

const decodeConfig = (raw: string): CustomerViewSearchConfig | null => {
  try {
    return JSON.parse(atob(raw)) as CustomerViewSearchConfig;
  } catch {
    return null;
  }
};

export const buildCustomerViewUrl = (
  config: CustomerViewSearchConfig
): string => {
  const params = new URLSearchParams({
    customerAccountNumber: config.customerAccountNumber,
    view: Views.GRID_VIEW,
    layout: JSON.stringify(false),
    config: btoa(JSON.stringify(config))
  });
  return `${ROUTES.PRODUCTS_CUSTOMER_VIEW}?${params}`;
};

const CustomerViewPage = () => {
  const [searchParams] = useSearchParams();
  const { companyData } = useAppContext();
  const { configuration } = useCatalogConfig();

  const accountNumber = searchParams.get('customerAccountNumber');

  const viewConfig = useMemo(() => {
    const raw = searchParams.get('config');
    return raw ? decodeConfig(raw) : null;
  }, [searchParams]);

  const {
    mutateAsync: fetchCustomer,
    data: customer,
    isLoading
  } = useMutation(getCustomerByUniqueValue());

  useEffect(() => {
    if (accountNumber) {
      fetchCustomer({ uniqueValue: accountNumber });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountNumber]);

  const configOverride: CatalogConfig = useMemo(() => {
    if (!configuration || !viewConfig) return configuration;

    const columnsSet = new Set(viewConfig.product.columns);
    const pricingSet = new Set(viewConfig.productDetails.pricing);
    const tabsSet = new Set(viewConfig.productDetails.tabs);

    const isColumnEnabled = (id: string) => columnsSet.has(id);

    const applyCustomerLabel = <T extends { id: string; label: string }>(
      item: T
    ): T => {
      const label = configuration.product?.columns?.find(c => c.id === item.id)
        ?.customerView?.columnLabel;
      return label ? { ...item, label } : item;
    };

    // Visible when the base config enables it AND
    // either the column isn't configurable or the customer view includes it
    const isLeadTimeVisible =
      !!configuration.product?.grid?.manufacturerLeadTime &&
      (!configuration.product?.columns?.some(
        c => c.id === ProductColumnsIds.LeadTime
      ) ||
        isColumnEnabled(ProductColumnsIds.LeadTime));

    return {
      ...configuration,
      filters: {
        ...configuration.filters,
        customer: {
          ...configuration.filters?.customer,
          show: viewConfig.filters.customer.show
        }
      },
      product: {
        ...configuration.product,
        columns: configuration.product?.columns?.filter(col =>
          isColumnEnabled(col.id)
        ),
        grid: {
          ...configuration.product?.grid,
          stock: {
            ...configuration.product?.grid?.stock,
            show: isColumnEnabled(ProductColumnsIds.Stock)
          },
          manufacturerSku:
            isColumnEnabled(ProductColumnsIds.Manufacturer) &&
            !!configuration.product?.grid?.manufacturerSku,
          manufacturerLeadTime: isLeadTimeVisible,
          pricing: configuration.product?.grid?.pricing
            ?.filter(p => isColumnEnabled(p.id))
            .map(applyCustomerLabel)
        }
      },
      productDetails: {
        ...configuration.productDetails,
        stock: {
          ...configuration.productDetails?.stock,
          show: isColumnEnabled(ProductColumnsIds.Stock)
        },
        pricing: configuration.productDetails?.pricing
          ?.filter(p => pricingSet.has(p.id))
          .map(applyCustomerLabel),
        tabs: configuration.productDetails?.tabs?.filter(tab =>
          tabsSet.has(tab.id)
        )
      }
    };
  }, [configuration, viewConfig]);

  if (!accountNumber || !viewConfig) return <InvalidConfiguration />;
  if (isLoading || !customer) return <PageLoader />;

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      style={{ backgroundColor: '#F8F9FA' }}>
      <Box
        px={4}
        py={3}
        display="flex"
        alignItems="center"
        style={{ backgroundColor: '#fff' }}>
        <SETVIImage
          src={
            companyData?.webAppLogoUrl ||
            companyData?.iphonelogourl ||
            FALLBACK_LOGO
          }
          alt="Company Logo"
          style={{ height: 40, borderBottom: '1px solid #EEF0F2' }}
        />
      </Box>
      <Products selectedCustomer={customer} configOverride={configOverride} />
    </Box>
  );
};

export default CustomerViewPage;
